import { useRef } from 'react';
import { cn } from '../../styles/lib/utils';
import { Badge } from '../Badge';
import { Text } from '../Text';

export type ScopeTabOption = {
  /** Stable value identifying the scope. */
  value: string;
  /** Display label. */
  label: string;
  /** Optional count shown in the trailing badge. */
  count?: number;
  /** When true, the tab is shown but not selectable (e.g. a scope not yet consumed). */
  disabled?: boolean;
};

export type ScopeTabsProps = {
  options: ScopeTabOption[];
  /** Currently selected scope value (controlled). */
  value: string;
  onValueChange: (value: string) => void;
  className?: string;
  /**
   * Layout direction. `'vertical'` (default) is the left-rail style used by the per-user Layer
   * Library. `'horizontal'` renders a top strip — for contexts (like org settings) that already
   * have their own left nav, where a second vertical rail would compete with it.
   */
  orientation?: 'vertical' | 'horizontal';
  'data-testid'?: string;
};

/**
 * The selection target for a vertical radiogroup keydown, or `null` when the key isn't a
 * navigation key. Arrow keys move to the next/previous **enabled** option (wrapping at the
 * ends); Home/End jump to the first/last enabled option. Pure + exported so the roving
 * keyboard behavior is unit-testable without a DOM render harness.
 */
export function nextScopeTabValue(
  options: readonly ScopeTabOption[],
  currentValue: string,
  key: string,
): string | null {
  const enabled = options.filter((option) => !option.disabled);
  if (enabled.length === 0) return null;
  const current = enabled.findIndex((option) => option.value === currentValue);
  const last = enabled.length - 1;

  switch (key) {
    case 'ArrowDown':
    case 'ArrowRight':
      return enabled[current < 0 ? 0 : (current + 1) % enabled.length].value;
    case 'ArrowUp':
    case 'ArrowLeft':
      return enabled[current < 0 ? last : (current - 1 + enabled.length) % enabled.length].value;
    case 'Home':
      return enabled[0].value;
    case 'End':
      return enabled[last].value;
    default:
      return null;
  }
}

/**
 * Scope selector for the Layer Library (e.g. CivilGrid Layers / Special Datasets).
 * Single-select like `RadioBar` (controlled `value` + `onValueChange`). Supports two
 * orientations: `'vertical'` (default) — a left rail styled like `RequestCard` rows, a
 * clickable row with a left accent bar that turns brand-orange for the active scope — and
 * `'horizontal'` — a top strip of inline tabs using a bottom-border underline as the active
 * indicator instead. Pure presentational; the consumer owns the value.
 *
 * A11y: it is genuinely single-select, so it uses the **radiogroup** pattern —
 * `role="radiogroup"` + `role="radio"`/`aria-checked`, roving `tabIndex` (only the active
 * row is in the tab order), and Arrow/Home/End keyboard navigation among enabled options
 * (unaffected by orientation — Up/Down and Left/Right both move next/previous).
 */
function ScopeTabs({
  options,
  value,
  onValueChange,
  className,
  orientation = 'vertical',
  'data-testid': dataTestId,
}: ScopeTabsProps) {
  const buttonRefs = useRef(new Map<string, HTMLButtonElement>());
  const hasActive = options.some((option) => option.value === value && !option.disabled);
  const firstEnabledValue = options.find((option) => !option.disabled)?.value;
  const isHorizontal = orientation === 'horizontal';

  const handleKeyDown = (event: React.KeyboardEvent<HTMLButtonElement>) => {
    const target = nextScopeTabValue(options, value, event.key);
    if (target === null) return;
    event.preventDefault();
    onValueChange(target);
    buttonRefs.current.get(target)?.focus();
  };

  return (
    <div
      role="radiogroup"
      aria-orientation={orientation}
      className={cn(isHorizontal ? 'flex flex-row items-center gap-1' : 'flex flex-col gap-0.5', className)}
      data-testid={dataTestId ?? 'scope-tabs'}
    >
      {options.map((option) => {
        const active = option.value === value;
        // Roving tab order: only the active row is tabbable; if nothing is active, the first
        // enabled row holds the tab stop so the group is reachable.
        const tabbable = active || (!hasActive && option.value === firstEnabledValue);
        return (
          // biome-ignore lint/a11y/useSemanticElements: the radiogroup ARIA pattern on a styled button is intentional — a native <input type="radio"> can't carry the row layout (accent bar, truncating label, trailing count badge).
          <button
            key={option.value}
            ref={(node) => {
              if (node) buttonRefs.current.set(option.value, node);
              else buttonRefs.current.delete(option.value);
            }}
            type="button"
            role="radio"
            aria-checked={active}
            tabIndex={option.disabled ? -1 : tabbable ? 0 : -1}
            disabled={option.disabled}
            onClick={() => onValueChange(option.value)}
            onKeyDown={handleKeyDown}
            className={cn(
              'flex items-center gap-2 rounded px-2 py-1.5 text-left transition-colors',
              'hover:bg-grey-10 focus-ring',
              'disabled:cursor-not-allowed disabled:opacity-50 disabled:hover:bg-transparent',
              isHorizontal
                ? cn('border-b-2', active ? 'border-orange-50' : 'border-transparent')
                : cn('w-full', active && 'bg-grey-10'),
            )}
            data-testid={`scope-tab-${option.value}`}
          >
            {/* Vertical only: selection accent bar, brand orange when active. Horizontal uses
                the button's own bottom-border underline as its active indicator instead. */}
            {!isHorizontal && (
              <span className={cn('h-4 w-0.5 shrink-0 rounded', active ? 'bg-orange-50' : 'bg-transparent')} />
            )}
            <Text className={cn('text-grey-90 flex-1 truncate text-sm', active ? 'font-semibold' : 'font-medium')}>
              {option.label}
            </Text>
            {option.count !== undefined && (
              <Badge kind="count" size="small" className="shrink-0">
                {option.count.toLocaleString()}
              </Badge>
            )}
          </button>
        );
      })}
    </div>
  );
}

export { ScopeTabs };
